import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { FERTILIZER_TYPES } from "@/data/fertilizer-types"
import { getCurrentSeason } from "./date-utils"
import {
  adjustNitrogenForSunlight,
  applyNutrientSafetyCaps,
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  fertilizerProductPoundsForNitrogen,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

export interface FertilizerResult extends DeterministicToolResultMetadata {
  grassType: string
  lawnSize: number
  fertilizerType: string
  season: 'spring' | 'summer' | 'fall' | 'winter'
  applicationGoal: string
  
  // NPK Requirements
  npkRequirements: {
    nitrogen: number // lbs per 1000 sq ft for this season
    phosphorus: number
    potassium: number
  }
  
  // Fertilizer Calculations
  fertilizerAmount: {
    pounds: number // total pounds needed
    bags: number // number of bags
    bagSize: number // size of each bag in pounds
    costEstimate: number // estimated cost
    costPerBag: number // estimated cost per bag
    costBreakdown?: { // optional detailed breakdown
      totalCost: number
      costPerPound: number
      priceTier: 'low' | 'mid' | 'high'
    }
  }
  
  // Application Details
  applicationDetails: {
    timing: string
    frequency: string
    method: string
    safetyTips: string[]
  }
  
  // Seasonal Recommendations
  seasonalAdvice: string
  nextApplication: string
  
  // Additional Info
  soilConsiderations: string[]
  environmentalTips: string[]
  warnings?: string[]

  // Timeline
  timeline: TimelineEvent[]
}

export interface TimelineEvent {
  month: number // 0-11 (January-December)
  monthName: string
  label: string // "Spring Application", "Fall Boost", etc.
  pounds: number
  fertilizer: string // "10-10-10 Balanced"
  npk: string // "10-10-10"
  soilTemp?: number // 55°F, 65°F, etc.
  notes: string
  isOptimal: boolean // Whether this is an optimal time vs acceptable time
}

export interface FertilizerCalculationInput {
  lawnSize: number | string
  grassType: string
  fertilizerType: string
  season: 'spring' | 'summer' | 'fall' | 'winter'
  applicationGoal?: 'maintenance' | 'establishment' | 'recovery' | 'seasonal'
  soilType?: 'clay' | 'sandy' | 'loamy' | 'unknown'
  sunlight?: string | null
}

export function calculateFertilizerRecommendations(input: FertilizerCalculationInput): FertilizerResult {
  const context = buildCanonicalToolContext({
    toolId: 'fertilizer',
    grassType: input.grassType,
    lawnSize: input.lawnSize,
    sunlight: input.sunlight,
  })
  const lawnSize = context.lawnSizeSqft ?? Number.NaN
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  const fertilizer = FERTILIZER_TYPES[input.fertilizerType]
  
  if (!grass) {
    throw new Error('Fertilizer data is not available for this grass type.')
  }

  if (!fertilizer) {
    throw new Error('This fertilizer type is not in our database.')
  }
  
  // Get grass NPK requirements
  const grassNPK = grass.fertilizationNeeds || {
    annualNPK: { nitrogen: 3.0, phosphorus: 1.5, potassium: 2.5 },
    seasonalSplit: { spring: 30, summer: 20, fall: 40, winter: 10 }
  }
  
  // Calculate seasonal NPK needs
  const seasonalMultiplier = grassNPK.seasonalSplit[input.season] / 100
  let npkPer1000SqFt = {
    nitrogen: grassNPK.annualNPK.nitrogen * seasonalMultiplier,
    phosphorus: grassNPK.annualNPK.phosphorus * seasonalMultiplier,
    potassium: grassNPK.annualNPK.potassium * seasonalMultiplier
  }
  
  // Apply application goal adjustments
  const goal = input.applicationGoal || 'maintenance'
  if (goal === 'establishment') {
    // New lawns need more phosphorus for root development
    npkPer1000SqFt.phosphorus *= 1.5
    npkPer1000SqFt.nitrogen *= 1.2
  } else if (goal === 'recovery') {
    // Recovering lawns need more nitrogen for growth
    npkPer1000SqFt.nitrogen *= 1.3
    npkPer1000SqFt.potassium *= 1.2 // Stress resistance
  } else if (goal === 'seasonal') {
    // Seasonal boost - adjust based on season
    if (input.season === 'fall') {
      npkPer1000SqFt.potassium *= 1.3 // Winter hardiness
    } else if (input.season === 'spring') {
      npkPer1000SqFt.nitrogen *= 1.2 // Spring green-up
    }
  }
  
  // Apply soil type adjustments (subtle adjustments)
  if (input.soilType === 'sandy') {
    // Sandy soils need slightly more frequent applications, but same rate
    // This is handled in advice, not calculation
  } else if (input.soilType === 'clay') {
    // Clay soils may need slightly less to avoid runoff
    npkPer1000SqFt.nitrogen *= 0.95
    npkPer1000SqFt.phosphorus *= 0.95
    npkPer1000SqFt.potassium *= 0.95
  }

  npkPer1000SqFt.nitrogen = adjustNitrogenForSunlight(npkPer1000SqFt.nitrogen, context.sunlight)

  // LSU's Carpetgrass guidance caps nitrogen at 1 lb per 1,000 sq ft per
  // year. The canonical data allocates that annual ceiling 50/50 between
  // spring and summer for planning. Goal, soil, and sunlight adjustments must
  // not raise the active-season ceiling above 0.5 lb.
  // Keep this guard species-specific. Existing grasses retain their current
  // goal and soil behavior.
  if (grass.name === 'Carpetgrass') {
    const carpetgrassSeasonCap = grassNPK.annualNPK.nitrogen * seasonalMultiplier
    npkPer1000SqFt.nitrogen = Math.min(npkPer1000SqFt.nitrogen, carpetgrassSeasonCap)
  }
  
  // Validate lawn size
  if (!lawnSize || isNaN(lawnSize) || lawnSize <= 0) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }

  if (lawnSize < 100) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }

  if (lawnSize > 217800) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }
  
  // Calculate total NPK needs for lawn
  const totalNPKNeeds = {
    nitrogen: (lawnSize / 1000) * npkPer1000SqFt.nitrogen,
    phosphorus: (lawnSize / 1000) * npkPer1000SqFt.phosphorus,
    potassium: (lawnSize / 1000) * npkPer1000SqFt.potassium
  }
  
  // Validate calculations
  if (!isFinite(totalNPKNeeds.nitrogen) || !isFinite(totalNPKNeeds.phosphorus) || !isFinite(totalNPKNeeds.potassium)) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }
  
  // Calculate fertilizer amounts needed (based on limiting nutrient)
  // Handle division by zero for fertilizers with 0% of a nutrient
  const fertilizerAmounts = {
    nitrogen: fertilizer.npkRatio.nitrogen > 0 
      ? fertilizerProductPoundsForNitrogen({
          targetNitrogenLbsPer1000: npkPer1000SqFt.nitrogen,
          nitrogenPercent: fertilizer.npkRatio.nitrogen,
          sqft: lawnSize,
        })
      : Infinity,
    phosphorus: fertilizer.npkRatio.phosphorus > 0
      ? totalNPKNeeds.phosphorus / (fertilizer.npkRatio.phosphorus / 100)
      : Infinity,
    potassium: fertilizer.npkRatio.potassium > 0
      ? totalNPKNeeds.potassium / (fertilizer.npkRatio.potassium / 100)
      : Infinity
  }
  
  // Use the highest requirement (limiting nutrient)
  // Filter out Infinity values (nutrients not in fertilizer)
  const validAmounts = Object.values(fertilizerAmounts).filter(amt => isFinite(amt))
  if (validAmounts.length === 0) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }

  let poundsNeeded = Math.max(...validAmounts)

  // Skip-this-season guard. Winter applications on cool-season grass and
  // dormant warm-season grass are explicitly not recommended in the advice
  // copy. For cool-season grasses (winter seasonalSplit > 0) the math
  // produces a positive `poundsNeeded` that we then zero out. For warm-
  // season grasses (winter seasonalSplit = 0) the math produces 0, which
  // would otherwise trip the `<= 0` throw below — that throw fires before
  // we get a chance to zero anything out, blowing up the request with
  // "Could not calculate fertilizer needs" instead of returning the
  // intended skip-season result. Hoisting the skip check above the throw
  // collapses both cases into a single deterministic skip path.
  // A season the grass data zeroes out (cool-season summer, warm-season fall/
  // winter) is a deliberate "do not feed" window, not an input error. Keying the
  // skip off seasonalMultiplier === 0 (in addition to winter) lets the agronomic
  // seasonalSplit drive the skip generically instead of a hardcoded season name.
  const skipThisSeason = input.season === 'winter' || seasonalMultiplier === 0
  if (skipThisSeason) {
    poundsNeeded = 0
  }

  // Validate pounds needed. Allow 0 only when the season is a deliberate
  // skip (above); any other 0/Infinity/NaN is an input problem.
  if (!isFinite(poundsNeeded) || (poundsNeeded <= 0 && !skipThisSeason)) {
    throw new Error('Could not calculate fertilizer needs. Please check your inputs.')
  }

  // Over-application guard. The limiting-nutrient math can over-recommend pounds
  // when the fertilizer's ratio for a non-limiting nutrient is small (e.g., 22-0-8
  // + grass needing trace K forces 60+ lbs of fert just for K, delivering 13+ lbs
  // of N — burn risk). applyNutrientSafetyCaps HARD-caps N, P AND K so no nutrient
  // exceeds the sourced over-application ceiling (currently 1.5x target; previously
  // only N was capped and P/K were warn-only, so a poor ratio could silently
  // over-deliver phosphorus/potassium). We accept under-delivery of the limiting
  // nutrient rather than exceed any nutrient's ceiling. Skip when zeroed for a
  // skip-season — no over-application possible at 0 lbs.
  let overApplicationWarnings: string[] = []
  if (!skipThisSeason) {
    const caps = applyNutrientSafetyCaps({
      poundsNeeded,
      npkRatio: fertilizer.npkRatio,
      target: totalNPKNeeds,
    })
    poundsNeeded = caps.poundsNeeded
    overApplicationWarnings = caps.warnings
  }

  // Cost estimation with bag sizes and market pricing
  const costPerPound = fertilizer.priceRange.mid // Use mid-range pricing as default
  const costEstimate = poundsNeeded * costPerPound
  const bagsNeeded = Math.ceil(poundsNeeded / fertilizer.bagSize)
  const costPerBag = fertilizer.bagSize * costPerPound
  
  // A season the grass data zeroes out but is NOT winter (cool-season summer,
  // warm-season fall) is a deliberate "do not feed" window. The season-name-based
  // generators below still emit feeding copy for it, so override the input-season
  // surfaces with a skip/hold message pointing at the next active feeding window.
  // (Winter already has correct skip copy in every generator's `case 'winter'`.)
  const isZeroSplitSkip = seasonalMultiplier === 0 && input.season !== 'winter'
  const nextActive = nextActiveFeedingSeason(grassNPK.seasonalSplit, input.season)

  // Generate seasonal advice
  const seasonalAdvice = isZeroSplitSkip
    ? `${grass.season}-season ${grass.name} is not fertilized in ${input.season} (no nitrogen need this season). Hold feeding until ${nextActive}.`
    : generateSeasonalAdvice(input.season, grass.season, input.applicationGoal)

  // Generate application details
  const applicationDetails = isZeroSplitSkip
    ? {
        timing: 'Not recommended this season',
        frequency: 'No applications',
        method: `Resume feeding at the next active window (${nextActive})`,
        safetyTips: ['Do not push nitrogen outside the active growth window'],
      }
    : generateApplicationDetails(input.season, grass.season, fertilizer.category)

  // Generate next application timing
  const nextApplication = isZeroSplitSkip
    ? `No application now. Next feeding in ${nextActive}.`
    : generateNextApplicationTiming(input.season, grass.season)

  // Generate soil considerations
  const soilConsiderations = generateSoilConsiderations(input.soilType, fertilizer.category)

  // Generate environmental tips
  const environmentalTips = generateEnvironmentalTips(fertilizer.category, lawnSize)

  // Generate timeline events, then drop any feeding event that falls in a season the
  // grass does not feed (seasonalSplit === 0) so the year view matches the advice
  // (e.g. no June feeding for cool-season, no October feeding for warm-season).
  const npkString = `${fertilizer.npkRatio.nitrogen}-${fertilizer.npkRatio.phosphorus}-${fertilizer.npkRatio.potassium}`
  const timeline = generateTimelineEvents(
    grass.name,
    grass.season,
    fertilizer.name,
    npkString,
    lawnSize,
    input.applicationGoal || 'maintenance'
  ).filter((ev) => (grassNPK.seasonalSplit[seasonOfMonth(ev.month)] ?? 0) > 0)

  return attachToolEngineMetadata({
    grassType: grass.name,
    lawnSize,
    fertilizerType: fertilizer.name,
    season: input.season,
    applicationGoal: input.applicationGoal || 'maintenance',
    
    npkRequirements: npkPer1000SqFt,
    
    fertilizerAmount: {
      pounds: Math.round(poundsNeeded * 10) / 10, // Round to 1 decimal
      bags: bagsNeeded,
      bagSize: fertilizer.bagSize,
      costEstimate: Math.round(costEstimate * 100) / 100, // Round to 2 decimals
      costPerBag: Math.round(costPerBag * 100) / 100,
      costBreakdown: {
        totalCost: Math.round(costEstimate * 100) / 100,
        costPerPound: costPerPound,
        priceTier: 'mid'
      }
    },
    
    applicationDetails,
    seasonalAdvice,
    nextApplication,
    soilConsiderations,
    environmentalTips,
    timeline,
    warnings: overApplicationWarnings.length > 0 ? overApplicationWarnings : undefined
  }, context)
}

type SeasonName = 'spring' | 'summer' | 'fall' | 'winter'

/** Calendar month (0-11) to season, matching the engine's currentSeason() mapping. */
function seasonOfMonth(month: number): SeasonName {
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

/** The next season (cyclically from `current`) whose seasonal split allocates nitrogen. */
function nextActiveFeedingSeason(
  split: { spring: number; summer: number; fall: number; winter: number },
  current: string,
): SeasonName {
  const order: SeasonName[] = ['spring', 'summer', 'fall', 'winter']
  const startIdx = Math.max(0, order.indexOf(current as SeasonName))
  for (let i = 1; i <= 4; i++) {
    const s = order[(startIdx + i) % 4]
    if ((split[s] ?? 0) > 0) return s
  }
  return 'spring'
}

function generateSeasonalAdvice(season: string, grassSeason: 'Cool' | 'Warm', goal?: string): string {
  if (grassSeason === 'Cool') {
    switch (season) {
      case 'spring':
        return "Spring is the ideal time for cool-season grass fertilization. Apply when soil temperature reaches 55°F and grass shows active growth. This promotes strong root development and prepares the lawn for summer stress."
      case 'summer':
        return "Light summer fertilization for cool-season grasses. Use slow-release formulas to avoid burning during heat stress. Consider iron supplements for color without growth stimulation."
      case 'fall':
        return "Fall fertilization is crucial for cool-season grasses. Apply in early fall to build root reserves and improve winter hardiness. This is often the most important application of the year."
      case 'winter':
        return "Winter fertilization is not recommended for cool-season grasses. Focus on leaf cleanup and prepare for spring applications."
      default:
        return "Follow seasonal timing recommendations for optimal results."
    }
  } else { // Warm season
    switch (season) {
      case 'spring':
        return "Begin fertilizing warm-season grasses when soil temperature reaches 65°F. Early spring applications promote green-up and prepare for the growing season."
      case 'summer':
        return "Summer is peak growing season for warm-season grasses. Regular fertilization every 4-6 weeks promotes lush growth and maintains color throughout the season."
      case 'fall':
        return "Reduce fertilization frequency in fall as warm-season grasses prepare for dormancy. Apply a final feeding 6-8 weeks before expected dormancy."
      case 'winter':
        return "Winter fertilization is not needed for dormant warm-season grasses. Focus on maintenance and prepare for spring green-up."
      default:
        return "Follow seasonal timing recommendations for optimal results."
    }
  }
}

function generateApplicationDetails(season: string, grassSeason: 'Cool' | 'Warm', fertilizerCategory: string): {
  timing: string
  frequency: string
  method: string
  safetyTips: string[]
} {
  const isCoolSeason = grassSeason === 'Cool'
  const isOrganic = fertilizerCategory === 'organic'
  
  let timing = ""
  let frequency = ""
  let method = "Apply evenly across lawn using a broadcast or drop spreader"
  
  if (isCoolSeason) {
    switch (season) {
      case 'spring':
        timing = "Apply when soil temperature reaches 55°F"
        frequency = "Every 6-8 weeks during growing season"
        break
      case 'summer':
        timing = "Early morning or evening to avoid heat stress"
        frequency = "Every 8-10 weeks (reduced rate)"
        break
      case 'fall':
        timing = "Early fall, 6-8 weeks before first frost"
        frequency = "Every 6-8 weeks during growing season"
        break
      case 'winter':
        timing = "Not recommended during winter"
        frequency = "No applications"
        break
    }
  } else { // Warm season
    switch (season) {
      case 'spring':
        timing = "When soil temperature reaches 65°F"
        frequency = "Every 4-6 weeks during growing season"
        break
      case 'summer':
        timing = "Early morning to avoid heat stress"
        frequency = "Every 4-6 weeks during peak growth"
        break
      case 'fall':
        timing = "Early fall before dormancy"
        frequency = "Every 6-8 weeks (reduced frequency)"
        break
      case 'winter':
        timing = "Not needed during dormancy"
        frequency = "No applications"
        break
    }
  }
  
  const safetyTips = [
    "Water thoroughly after application",
    "Keep children and pets off lawn for 24-48 hours",
    "Store fertilizer in a cool, dry place",
    "Clean spreader after use",
    "Follow label instructions carefully"
  ]
  
  if (isOrganic) {
    safetyTips.push("Organic fertilizers are safer for pets and children")
  } else {
    safetyTips.push("Wear gloves and protective clothing during application")
  }
  
  return {
    timing,
    frequency,
    method,
    safetyTips
  }
}

function generateNextApplicationTiming(season: string, grassSeason: 'Cool' | 'Warm'): string {
  const isCoolSeason = grassSeason === 'Cool'
  
  if (isCoolSeason) {
    switch (season) {
      case 'spring':
        return "Next application in 6-8 weeks (early summer)"
      case 'summer':
        return "Next application in 8-10 weeks (early fall)"
      case 'fall':
        return "Next application in 6-8 weeks (late fall) or wait for spring"
      case 'winter':
        return "Next application in spring when soil reaches 55°F"
    }
  } else { // Warm season
    switch (season) {
      case 'spring':
        return "Next application in 4-6 weeks (early summer)"
      case 'summer':
        return "Next application in 4-6 weeks (late summer)"
      case 'fall':
        return "Next application in 6-8 weeks (late fall) or wait for spring"
      case 'winter':
        return "Next application in spring when soil reaches 65°F"
    }
  }
  
  return "Follow seasonal timing recommendations"
}

function generateSoilConsiderations(soilType?: string, fertilizerCategory?: string): string[] {
  const considerations = []
  
  if (soilType === 'clay') {
    considerations.push("Clay soils may need more frequent, lighter applications")
    considerations.push("Improve drainage before heavy fertilization")
  } else if (soilType === 'sandy') {
    considerations.push("Sandy soils require more frequent applications")
    considerations.push("Consider slow-release fertilizers for better retention")
  } else if (soilType === 'loamy') {
    considerations.push("Loamy soils are ideal for most fertilization programs")
  }
  
  if (fertilizerCategory === 'organic') {
    considerations.push("Organic fertilizers improve soil structure over time")
    considerations.push("May take longer to see results compared to synthetic")
  }
  
  return considerations
}

function generateTimelineEvents(
  grassType: string,
  grassSeason: string,
  fertilizerName: string,
  npkRatio: string,
  lawnSize: number,
  applicationGoal: string
): TimelineEvent[] {
  const events: TimelineEvent[] = []
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  // Determine pounds per application (divide annual needs by number of applications)
  const isCoolSeason = grassSeason === 'Cool'
  const applicationsPerYear = applicationGoal === 'establishment' ? 5 : (isCoolSeason ? 4 : 4)
  const poundsPerApp = Math.round((lawnSize / 1000) * 2.5 * 10) / 10 // Rough estimate

  if (isCoolSeason) {
    // Cool-season grasses: Heavy spring and fall applications
    // Spring applications
    events.push({
      month: 3, // April
      monthName: 'April',
      label: 'Spring Application',
      pounds: poundsPerApp,
      fertilizer: fertilizerName,
      npk: npkRatio,
      soilTemp: 55,
      notes: 'Apply when soil temperature reaches 55°F. Promotes spring green-up and growth.',
      isOptimal: true
    })

    events.push({
      month: 5, // June
      monthName: 'June',
      label: 'Early Summer Boost',
      pounds: poundsPerApp * 0.7,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Light application to maintain color. Avoid over-fertilizing in summer heat.',
      isOptimal: false
    })

    // Fall applications (most important for cool-season)
    events.push({
      month: 8, // September
      monthName: 'September',
      label: 'Fall Application',
      pounds: poundsPerApp * 1.2,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Most important application. Promotes root growth and winter hardiness.',
      isOptimal: true
    })

    events.push({
      month: 10, // November
      monthName: 'November',
      label: 'Late Fall Boost',
      pounds: poundsPerApp,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Final application before dormancy. Enhances spring recovery.',
      isOptimal: true
    })
  } else {
    // Warm-season grasses: Applications during active growing season
    events.push({
      month: 4, // May
      monthName: 'May',
      label: 'Spring Green-Up',
      pounds: poundsPerApp,
      fertilizer: fertilizerName,
      npk: npkRatio,
      soilTemp: 65,
      notes: 'Apply when soil temperature consistently reaches 65°F. Promotes active growth.',
      isOptimal: true
    })

    events.push({
      month: 6, // July
      monthName: 'July',
      label: 'Summer Application',
      pounds: poundsPerApp,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Peak growing season. Maintains color and density.',
      isOptimal: true
    })

    events.push({
      month: 7, // August
      monthName: 'August',
      label: 'Late Summer Boost',
      pounds: poundsPerApp * 0.8,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Lighter application to sustain growth as temperatures moderate.',
      isOptimal: false
    })

    events.push({
      month: 9, // October
      monthName: 'October',
      label: 'Fall Winterization',
      pounds: poundsPerApp * 0.7,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Light application to prepare for dormancy. Helps with winter color in mild climates.',
      isOptimal: false
    })
  }

  // Add extra application for establishment goal
  if (applicationGoal === 'establishment' && isCoolSeason) {
    events.push({
      month: 2, // March
      monthName: 'March',
      label: 'Early Spring Starter',
      pounds: poundsPerApp * 0.8,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Starter application for new lawns. Use high-phosphorus fertilizer for root development.',
      isOptimal: true
    })
  } else if (applicationGoal === 'establishment' && !isCoolSeason) {
    events.push({
      month: 5, // June
      monthName: 'June',
      label: 'Establishment Boost',
      pounds: poundsPerApp * 0.8,
      fertilizer: fertilizerName,
      npk: npkRatio,
      notes: 'Additional application for new lawns. Promotes quick establishment.',
      isOptimal: true
    })
  }

  // Sort by month
  return events.sort((a, b) => a.month - b.month)
}

function generateEnvironmentalTips(fertilizerCategory?: string, lawnSize?: number): string[] {
  const tips = [
    "Sweep fertilizer off sidewalks and driveways to prevent runoff",
    "Avoid fertilizing before heavy rain",
    "Consider soil testing for precise nutrient needs"
  ]

  if (lawnSize && lawnSize > 5000) {
    tips.push("Large lawns may benefit from professional application services")
  }
  
  if (fertilizerCategory === 'organic') {
    tips.push("Organic fertilizers are more environmentally friendly")
  }
  
  return tips
}
